import { useMemo } from "react";
import { lib, groupSchemaIntoSections, emptySelection } from "../lib/form";
import type { Selection } from "../lib/types";

interface Props {
  selection: Selection;
  onChange: (next: Selection) => void;
  onPreview: () => void;
}

function hasAnySelection(s: Selection): boolean {
  if (s.classDate) return true;
  if (Object.values(s.selects).some((v) => v && v !== "")) return true;
  if (Object.values(s.checkboxes).some((arr) => arr && arr.length > 0)) return true;
  return false;
}

export function Form({ selection, onChange, onPreview }: Props) {
  const sections = useMemo(
    () => groupSchemaIntoSections(lib.schema),
    []
  );

  function setSelect(name: string, value: string) {
    onChange({
      ...selection,
      selects: { ...selection.selects, [name]: value },
    });
  }
  function toggleCheckbox(groupName: string, value: string) {
    const cur = selection.checkboxes[groupName] ?? [];
    const next = cur.includes(value)
      ? cur.filter((v) => v !== value)
      : [...cur, value];
    onChange({
      ...selection,
      checkboxes: { ...selection.checkboxes, [groupName]: next },
    });
  }

  function handleClear() {
    if (!hasAnySelection(selection)) return;
    if (window.confirm("Clear all selections and start over?")) {
      onChange(emptySelection());
    }
  }

  const dirty = hasAnySelection(selection);

  return (
    <main className="form">
      <div className="form__topBar">
        <button
          type="button"
          onClick={handleClear}
          className="btn btn--ghost form__clear"
          disabled={!dirty}
          aria-label="Clear all selections"
        >
          Clear form
        </button>
      </div>
      <h1 className="form__title">Workout Generator</h1>
      <p className="form__subtitle">
        Build a custom barre-style class plan in under a minute.
      </p>

      <section className="instructions">
        <h2 className="instructions__title">How it works</h2>
        <ol className="instructions__list">
          <li>Pick a date for your class.</li>
          <li>
            Walk through each section below and choose the moves you want to
            include — selects pick one option, checkboxes let you pick multiple.
          </li>
          <li>
            Click <strong>Preview</strong> to see your full workout in canonical
            class order.
          </li>
          <li>
            Click <strong>Download</strong> to generate a printable PDF. Your
            selections auto-save to this browser, so you can come back later.
          </li>
        </ol>
      </section>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onPreview();
        }}
        className="form__body"
      >
        <div className="form__grid form__topRow">
          <label className="field field--half">
            <span className="field__label">
              Date of Class <em className="field__required">(Required)</em>
            </span>
            <input
              type="date"
              required
              value={selection.classDate}
              onChange={(e) =>
                onChange({ ...selection, classDate: e.target.value })
              }
            />
          </label>
        </div>

        {sections.map((section, sIdx) => (
          <section key={sIdx} className="form__section">
            {section.title && (
              <h2 className="form__sectionTitle">{section.title}</h2>
            )}

            <div className="form__grid">
              {section.fields.map((f) => {
                const widthClass = f.width === "half" ? "field--half" : "field--full";
                if (f.kind === "select") {
                  const inputId = f.name.replace("input_", "");
                  return (
                    <label key={f.fieldId} className={`field ${widthClass}`}>
                      <span className="field__label">{f.label}</span>
                      <select
                        value={selection.selects[inputId] ?? ""}
                        onChange={(e) => setSelect(inputId, e.target.value)}
                      >
                        {f.options.map((o, i) => (
                          <option key={i} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                }
                if (f.kind === "checkbox-group") {
                  const groupName = f.choices[0]?.name.split(".")[0].replace("input_", "") ?? "";
                  return (
                    <fieldset key={f.fieldId} className={`field ${widthClass} checkboxes`}>
                      <legend className="field__label">{f.legend}</legend>
                      <div className="checkboxes__list">
                        {f.choices.map((c, i) => {
                          const checked =
                            selection.checkboxes[groupName]?.includes(c.value) ?? false;
                          return (
                            <label key={i} className="checkbox">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCheckbox(groupName, c.value)}
                              />
                              <span>{c.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  );
                }
                return null;
              })}
            </div>
          </section>
        ))}

        <div className="form__actions">
          <button type="submit" className="btn btn--primary">
            Preview
          </button>
        </div>
      </form>
    </main>
  );
}
